/**
 * useRoomCreator: shared "POST a room endpoint, then navigate into it" logic.
 *
 * Both landing CTAs create a room the same way: POST an endpoint with the WS
 * session bearer token, read back `{ roomId }`, and route to /r/CODE. This was
 * one `postRoom(endpoint, which)` function on Splash; when the Free Draw CTA
 * moved to /about (item 1) the logic needed a second home, so it lives here as
 * one hook instead of being copied.
 *
 *   - "/api/room"      → Create a game
 *   - "/api/free-draw" → Free Draw (now on /about)
 *
 * Same-origin only: Vite proxies /api to Fastify in dev, and prod behind nginx
 * is same-origin, so there is no CORS surface.
 */

import { useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import type { ConnState } from "../ws.js";

type RoomEndpoint = "/api/room" | "/api/free-draw";

export type RoomCreator = {
  /** POST the endpoint and navigate to the created room. No-op until `ready`. */
  create: (endpoint: RoomEndpoint) => Promise<void>;
  /** True while a create request is in flight (drives the "Creating…" label). */
  creating: boolean;
  /** Last create error, or null. */
  error: string | null;
  /** True once the WS session is established (a room can be created). */
  ready: boolean;
};

export function useRoomCreator(connState: ConnState): RoomCreator {
  const { route } = useLocation();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = connState.kind === "connected" || connState.kind === "in-room";
  const sessionToken = ready ? connState.sessionToken : null;

  async function create(endpoint: RoomEndpoint): Promise<void> {
    if (!sessionToken || creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({}),
      });
      // Audit 2026-06-11: users saw "Server returned 500" verbatim.
      if (!res.ok) throw new Error("Couldn't make a room — try again in a sec.");
      const data = (await res.json()) as { roomId: string };
      route(`/r/${data.roomId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create room");
      setCreating(false);
    }
  }

  return { create, creating, error, ready };
}
